import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import Icon from './Icon.jsx'
import { useAuth } from '../contexts/AuthContext.jsx'
import { useEscapeKey } from '../lib/useEscapeKey.js'

const POP_WIDTH = 320
const POP_PAD = 11 // .cell-pop's own padding, both sides — see styles.css
// Opening the note widens the popover rather than lengthening it: the board is a wide,
// short table and the popover is anchored to a cell in it, so growing downwards is what
// pushes the thing you are annotating off screen.
const POP_WIDTH_NOTE = 600

/**
 * Booking a job, as a popover anchored to the cell you clicked.
 *
 * It used to be a form crammed inside the table cell, which meant the row grew as you typed,
 * the column widths shifted under the pointer, and the job type was a native <select> — three
 * clicks and a scroll to pick from a list of eight. The popover floats above the board
 * instead, so nothing moves, and the job type is a row of pills you hit once.
 *
 * Still deliberately small. The whole product replaces a whiteboard, and the person using it
 * is filling forty cells before the crews leave: a label, optionally a job type, done.
 */
export default function CellEditor({
  entry,
  jobTypes,
  staffJobTypeIds,
  labels,
  staffName,
  slot,
  dateLabel,
  anchor,
  onSave,
  onDelete,
  onCancel,
  crews = null,
  defaultCrewId = null,
}) {
  const [label, setLabel] = useState(entry?.label || '')
  const [jobTypeId, setJobTypeId] = useState(entry?.jobTypeId || '')
  const [internalNote, setInternalNote] = useState(entry?.internalNote || '')
  // Only present when the board is grouped by person: there the row says who, and nothing
  // says which crew, so the job has to carry it. Grouped by crew the band already answers
  // it and a picker here would be a second way to say the same thing.
  const [crewId, setCrewId] = useState(entry?.crewGroupId ?? entry?.catTeamId ?? defaultCrewId ?? '')
  // The tenant's own word for the mode, not a hard-coded one: a company that renamed Group
  // to "Storm" should be picking a Storm here, the same as everywhere else on the board.
  const { modeName } = useAuth()
  // A booking that already carries a note opens with it showing. Hiding an existing note
  // behind a button nobody has a reason to press is how it gets edited away by accident.
  const [noteOpen, setNoteOpen] = useState(Boolean(entry?.internalNote))
  const width = noteOpen ? POP_WIDTH_NOTE : POP_WIDTH
  const inputRef = useRef(null)
  const popRef = useRef(null)
  // Below the cell, or above it — decided ONCE, when the popover opens, and then kept.
  // Its height changes as you type (the suggestion list appears, a warning shows), and
  // re-deciding on every keystroke made it flip sides under the pointer.
  const sideRef = useRef(null)
  const [pos, setPos] = useState(null)

  useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [])

  // Measure, then place: below the cell when there is room, above it when there is not, and
  // never off the side. Hidden until placed, or it visibly jumps on open.
  useLayoutEffect(() => {
    const el = popRef.current
    if (!el || !anchor) return
    const h = el.offsetHeight
    const vw = window.innerWidth
    const vh = window.innerHeight
    // Placed once and then left alone — see AssetCellEditor for why. It scrolls inside itself
    // if it outgrows the room beneath rather than hopping while somebody types into it.
    if (!sideRef.current) {
      sideRef.current = anchor.bottom + 8 + h <= vh - 8 ? 'below' : 'above'
    }
    const top = sideRef.current === 'below' ? anchor.bottom + 8 : Math.max(8, anchor.top - h - 8)
    // Anchored on the CLOSED width and then held, so opening the note unfolds to the right
    // rather than growing in both directions — see AssetCellEditor for the full reasoning.
    // It shifts only when the widened popover would run off the right edge.
    const anchored = Math.max(8, Math.min(anchor.left, vw - POP_WIDTH - 8))
    setPos({
      top,
      left: Math.max(8, Math.min(anchored, vw - width - 8)),
      maxHeight: Math.max(160, vh - top - 8),
    })
  }, [anchor, label, jobTypeId, width, noteOpen])

  useEscapeKey(onCancel)

  // Qualified job types first — the ones this person actually does are the ones being
  // reached for, and a list that puts them last makes the common case the slow one.
  const orderedTypes = useMemo(() => {
    if (!staffJobTypeIds) return jobTypes
    const qualified = (j) => staffJobTypeIds.includes(j.id)
    return [...jobTypes].sort((a, b) => Number(qualified(b)) - Number(qualified(a)))
  }, [jobTypes, staffJobTypeIds])

  // What's already on the board today — not a history of every label ever typed, which
  // drifts further from relevant the longer the company uses this and starts suggesting
  // jobs from three months ago. `labels` is handed in already scoped to today's date.
  const suggestions = useMemo(() => {
    const q = label.trim().toLowerCase()
    return (labels || [])
      .filter((l) => l.toLowerCase() !== q && (!q || l.toLowerCase().includes(q)))
      .slice(0, 5)
  }, [labels, label])

  const notQualified = jobTypeId && staffJobTypeIds && !staffJobTypeIds.includes(jobTypeId)

  /**
   * Whether anything here differs from what was opened. A click-out on an untouched popover
   * should cost nothing — without this every glance at a booking would PATCH it, spending a
   * request, an audit entry and a version bump to write back the values it already had.
   */
  const dirty =
    label.trim() !== (entry?.label || '') ||
    (jobTypeId || '') !== (entry?.jobTypeId || '') ||
    internalNote.trim() !== (entry?.internalNote || '') ||
    (crews ? String(crewId || '') !== String(entry?.crewGroupId ?? entry?.catTeamId ?? defaultCrewId ?? '') : false)

  /**
   * Clicking away from the popover keeps the work, rather than throwing it away — filling a
   * board is forty of these in a row and reaching for Save each time is most of the effort.
   *
   * Two things it deliberately does NOT do. It never deletes: an empty box means "delete
   * this" when you press Save, and inheriting that here would turn selecting the text,
   * fumbling a key and clicking elsewhere into a booking silently disappearing. And it never
   * writes when nothing changed. Cancel, the X and Escape still discard — walking away and
   * saying no are different intentions, and only one of them should be guessed at.
   */
  function commit() {
    if (!label.trim() || !dirty) return onCancel()
    submit()
  }

  function submit(e) {
    e?.preventDefault()
    const trimmed = label.trim()
    // Clearing the text of an existing booking is how you delete it — the same gesture as
    // wiping a cell on the whiteboard this replaces.
    if (!trimmed) return entry ? onDelete() : onCancel()
    // Always sent, so emptying the box clears the note. The repo reads an absent field as
    // "leave it alone", which is what a drag-copy relies on.
    onSave({
      label: trimmed,
      jobTypeId: jobTypeId || null,
      internalNote: internalNote.trim(),
      // Sent only when this editor was given a crew list — otherwise it must not be in the
      // patch at all, or a save from the crew-grouped board would rewrite the crew to
      // whatever this field happened to hold.
      ...(crews ? { catTeamId: crewId || null } : {}),
    })
  }

  return (
    <div
      className="cell-pop-backdrop"
      onClick={commit}
      onContextMenu={(e) => { e.preventDefault(); commit() }}
    >
      <form
        ref={popRef}
        className={`cell-pop ${noteOpen ? 'with-note' : ''}`}
        // The left column is pinned to the width it has when the note is closed, published
        // here so the number lives with POP_WIDTH rather than being copied into the
        // stylesheet. Without it the two columns split the widened popover evenly, the job
        // types column came out NARROWER than it had been, its chips rewrapped onto another
        // row, and the popover grew taller — the one thing widening it exists to avoid.
        style={{
          width,
          '--pop-main-w': `${POP_WIDTH - POP_PAD * 2}px`,
          ...(pos || { top: -9999, left: -9999 }),
        }}
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <div className="cell-pop-head">
          <div className="cell-pop-who">
            <strong>{staffName}</strong>
            <span>Job {slot}{dateLabel ? ` · ${dateLabel}` : ''}</span>
          </div>
          {/* Sits with the title rather than down in the footer with Save and Cancel: it
              opens a second panel, which is a different kind of act from committing or
              abandoning the form. */}
          <button
            type="button"
            className={`cell-pop-note-btn ${noteOpen ? 'on' : ''}`}
            onClick={() => setNoteOpen((v) => !v)}
            aria-pressed={noteOpen}
            title={
              noteOpen
                ? 'Hide the internal note'
                : 'A private note for whoever is running the board'
            }
          >
            <Icon name="note" size={13} strokeWidth={2.2} />
            Internal Note
            {/* A dot, not a count: there is only ever one note, and the question being
                answered from a glance is whether this booking already has one. */}
            {!noteOpen && internalNote.trim() && <span className="cell-pop-note-dot" />}
          </button>
          <button type="button" className="cell-pop-x" onClick={onCancel} aria-label="Close">
            <Icon name="close" size={14} strokeWidth={2.2} />
          </button>
        </div>

        <div className="cell-pop-body">
        <div className="cell-pop-main">

        <input
          ref={inputRef}
          className="cell-pop-input"
          type="text"
          maxLength={120}
          value={label}
          placeholder="What are they doing?"
          onChange={(e) => setLabel(e.target.value)}
        />

        {suggestions.length > 0 && (
          <div className="cell-pop-section cell-pop-section-tight">
            <div className="cell-pop-legend">Suggested names</div>
            <div className="pill-row">
              {suggestions.map((l) => (
                <button key={l} type="button" className="pill-toggle sm" onClick={() => setLabel(l)}>
                  {l}
                </button>
              ))}
            </div>
          </div>
        )}

        {crews && (
          <div className="cell-pop-section">
            <div className="cell-pop-legend">{modeName('cat')}</div>
            <div className="pill-row">
              <button
                type="button"
                className={`pill-toggle ${!crewId ? 'on' : ''}`}
                onClick={() => setCrewId('')}
                title={`Not on a ${modeName('cat').toLowerCase()}`}
              >
                None
              </button>
              {crews.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={`pill-toggle crew-pill ${crewId === c.id ? 'on' : ''}`}
                  // The crew's own colour on the pill, so picking one here and reading it
                  // back off the board later are the same recognition.
                  style={c.color ? { '--crew': c.color } : undefined}
                  onClick={() => setCrewId(crewId === c.id ? '' : c.id)}
                >
                  {c.color && <span className="group-swatch" style={{ background: c.color }} />}
                  {c.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Nothing at all when the company has not set any up: a legend, a None pill and a
            line explaining the absence is three pieces of furniture around a choice that
            cannot be made. Somebody booking work does not need telling here that job types
            exist — Settings is where they would be added. */}
        {jobTypes.length > 0 && (
        <div className="cell-pop-section">
          <div className="cell-pop-legend">Job type</div>
          <div className="pill-row">
            <button
              type="button"
              className={`pill-toggle ${!jobTypeId ? 'on' : ''}`}
              onClick={() => setJobTypeId('')}
            >
              None
            </button>
            {orderedTypes.map((j) => {
              const unqualified = staffJobTypeIds && !staffJobTypeIds.includes(j.id)
              return (
                <button
                  key={j.id}
                  type="button"
                  className={`pill-toggle ${jobTypeId === j.id ? 'on' : ''} ${unqualified ? 'unqualified' : ''}`}
                  onClick={() => setJobTypeId(jobTypeId === j.id ? '' : j.id)}
                  title={unqualified ? 'Not marked as qualified for this' : undefined}
                >
                  {j.name}
                </button>
              )
            })}
          </div>
        </div>
        )}

        {/* Advisory, not a block: refusing the save just gets worked around by leaving the
            job type blank, which loses the information entirely. */}
        {notQualified && (
          <div className="cell-pop-warn">
            <Icon name="alert" size={14} />
            Not marked as qualified — saving anyway.
          </div>
        )}

        </div>

        {noteOpen && (
          <div className="cell-pop-note">
            <div className="cell-pop-legend">Internal note</div>
            <textarea
              className="cell-pop-textarea"
              maxLength={2000}
              value={internalNote}
              onChange={(e) => setInternalNote(e.target.value)}
            />
          </div>
        )}
        </div>

        <div className="cell-pop-foot">
          {entry ? (
            <button type="button" className="btn sm ghost danger" onClick={onDelete}>
              <Icon name="trash" size={14} />
              Delete
            </button>
          ) : (
            <span className="cell-pop-hint">Enter to save</span>
          )}
          <span className="spacer" />
          <button type="button" className="btn sm" onClick={onCancel}>Cancel</button>
          <button type="submit" className="btn sm primary">{entry ? 'Save' : 'Add job'}</button>
        </div>
      </form>
    </div>
  )
}
